import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "@/api/apiClient";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Filter, ArrowLeft } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import MatchCard from "../components/matches/MatchCard";
import FilterPanel from "../components/swipe/FilterPanel";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { normalizeCategory } from "@/utils/categoryUtils";
import { normalizeColor, getColorName } from "@/utils/colorUtils";
import SkeletonGrid from "../components/ui/SkeletonGrid";


// Default Seed Generator
const generateSeed = () => Math.floor(Math.random() * 1000000).toString();

// Hook for infinite scroll detection using correct container
function useInfiniteScroll(callback, isLoading, hasMore) {
    const isLoadingRef = React.useRef(isLoading);
    const hasMoreRef = React.useRef(hasMore);
    const callbackRef = React.useRef(callback);

    React.useEffect(() => {
        isLoadingRef.current = isLoading;
        hasMoreRef.current = hasMore;
        callbackRef.current = callback;
    }, [isLoading, hasMore, callback]);

    React.useEffect(() => {
        let lastTriggerTime = 0;
        const container = document.getElementById('main-scroll-container') || window;

        const handleScroll = () => {
            const loading = isLoadingRef.current;
            const more = hasMoreRef.current;

            if (loading || !more) return;

            const now = Date.now();
            if (now - lastTriggerTime < 500) return;

            let scrollTop, clientHeight, scrollHeight;

            if (container === window) {
                scrollTop = window.scrollY || document.documentElement.scrollTop;
                clientHeight = window.innerHeight;
                scrollHeight = document.documentElement.scrollHeight;
            } else {
                scrollTop = container.scrollTop;
                clientHeight = container.clientHeight;
                scrollHeight = container.scrollHeight;
            }

            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

            // Trigger when within 300px
            if (distanceFromBottom < 300) {

                lastTriggerTime = now;
                callbackRef.current();
            }
        };

        const checkInitial = () => {
            if (!isLoadingRef.current && hasMoreRef.current) {
                let clientHeight, scrollHeight;
                if (container === window) {
                    clientHeight = window.innerHeight;
                    scrollHeight = document.documentElement.scrollHeight;
                } else {
                    clientHeight = container.clientHeight;
                    scrollHeight = container.scrollHeight;
                }

                if (scrollHeight <= clientHeight + 100) {
                    callbackRef.current();
                }
            }
        };

        setTimeout(checkInitial, 500);

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, []);
}

export default function RecherchePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    // -------------------------------------------------------------------------
    // 1. HIDDEN STATE (History API) - Seed & Pagination
    // -------------------------------------------------------------------------

    // We try to read from location.state. logic:
    // - If it exists, use it.
    // - If not, generate defaults.
    // - We DO NOT sync this to URL query params.
    const seed = useMemo(() => location.state?.seed || generateSeed(), [location.state?.seed]);
    const [displayLimit, setDisplayLimit] = useState(24);

    // Ensure history has our state initialized (for "Back" button stability)
    useEffect(() => {
        if (!location.state?.seed || !location.state?.limit) {
            setSearchParams(searchParams, {
                replace: true,
                state: {
                    ...location.state,
                    seed: seed,
                    limit: displayLimit
                }
            });
        }
    }, []); // Only check on mount

    // -------------------------------------------------------------------------
    // 2. PUBLIC STATE (URL Query Params) - Filters & Search
    // -------------------------------------------------------------------------

    // Helper to get raw values safely
    const getParam = (key) => searchParams.get(key);
    const getList = (key) => {
        const val = searchParams.get(key);
        return val ? val.split(",") : [];
    };

    // Derived State (Source of Truth = URL)
    const searchQuery = getParam("q") || "";

    // Complex Filters object derived from URL
    const filters = useMemo(() => ({
        minPrice: Number(getParam("min_price")) || 0,
        maxPrice: Number(getParam("max_price")) || 10000,
        categories: getList("categories"),
        subcategories: getList("subcategories"),
        colors: getList("colors"),
        brands: getList("brands").length > 0 ? getList("brands") : (getParam("brand") ? [getParam("brand")] : []),
        sizes: getList("sizes"),
        inStockOnly: getParam("in_stock") === "true",
        is_new: getParam("new") === "true",
    }), [searchParams]);

    // -------------------------------------------------------------------------
    // 3. SETTERS (Update URL directly)
    // -------------------------------------------------------------------------

    // Helper to serialize filters back to params
    const serializeParams = (newFilters, newQuery) => {
        const params = {};

        // Search
        if (newQuery) params.q = newQuery;

        // Filters
        if (newFilters.minPrice > 0) params.min_price = newFilters.minPrice.toString();
        if (newFilters.maxPrice < 10000) params.max_price = newFilters.maxPrice.toString();
        if (newFilters.categories.length > 0) params.categories = newFilters.categories.join(",");
        if (newFilters.subcategories.length > 0) params.subcategories = newFilters.subcategories.join(",");
        if (newFilters.colors.length > 0) params.colors = newFilters.colors.join(",");
        if (newFilters.brands.length > 0) params.brands = newFilters.brands.join(",");
        if (newFilters.sizes.length > 0) params.sizes = newFilters.sizes.join(",");
        if (newFilters.inStockOnly) params.in_stock = "true";
        if (newFilters.is_new) params.new = "true";

        // Branding/Legacy
        if (newFilters.brands.length === 1 && !params.brand) params.brand = newFilters.brands[0];

        return params;
    };

    const updateURL = (newParams, keepHistoryState = true, newLimit = displayLimit) => {
        setSearchParams(newParams, {
            replace: true,
            state: keepHistoryState ? { ...location.state, seed, limit: newLimit } : undefined // Preserve hidden state
        });
    };

    // Adapter for FilterPanel (which expects setFilters(prev => ...))
    const setFilters = (update) => {
        let newFilters;
        if (typeof update === 'function') {
            newFilters = update(filters);
        } else {
            newFilters = update;
        }

        // Reset limit when filters change
        setDisplayLimit(24);

        const params = serializeParams(newFilters, searchQuery);
        updateURL(params, true, 24);
    };

    // Local state for debounced search
    const [localQuery, setLocalQuery] = useState(searchQuery);

    // Sync local state when URL changes (e.g. navigation)
    useEffect(() => {
        setLocalQuery(searchQuery);
    }, [searchQuery]);

    // Debounce URL update
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localQuery !== searchQuery) {
                setDisplayLimit(24);
                const params = serializeParams(filters, localQuery);
                setSearchParams(params, { replace: true, state: { ...location.state, seed, limit: 24 } });
            }
        }, 400); // 400ms debounce

        return () => clearTimeout(timer);
    }, [localQuery, filters, searchQuery, setSearchParams, location.state, seed]);

    // Update local state immediately
    const handleSearchChange = (val) => {
        setLocalQuery(val);
    };

    const handleClearSearch = () => {
        setLocalQuery("");
        // Immediate clear for better UX
        setDisplayLimit(24);
        const params = serializeParams(filters, "");
        setSearchParams(params, { replace: true, state: { ...location.state, seed, limit: 24 } });
    };

    const setShowNewOnlyWrapper = (val) => {
        setFilters(prev => ({ ...prev, is_new: val }));
    };

    const handleReset = useCallback(() => {
        // Reset everything
        setDisplayLimit(24);
        setSearchParams({});
    }, [setSearchParams]);

    // Atomic update for "Voir plus" (Navigate to Brand Page)
    const handleSeeMore = useCallback((brand) => {
        navigate(`/brand/${brand}`);
    }, [navigate]);


    // -------------------------------------------------------------------------
    // 4. DATA FETCHING & LOGIC
    // -------------------------------------------------------------------------

    // -------------------------------------------------------------------------
    // 4. DATA FETCHING & LOGIC
    // -------------------------------------------------------------------------

    const [showFilters, setShowFilters] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.matchMedia("(max-width: 768px)").matches);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Default Discovery Check
    // Relaxed check to match useQuery logic (allow maxPrice >= 500)
    const isDefaultView = !searchQuery &&
        (filters.minPrice === 0 || filters.minPrice === undefined) &&
        (filters.maxPrice === 10000 || filters.maxPrice >= 500) &&
        filters.categories.length === 0 && filters.subcategories.length === 0 &&
        filters.colors.length === 0 && filters.brands.length === 0 &&
        filters.sizes.length === 0 && !filters.inStockOnly && !filters.is_new;

    const { data: products = [], isLoading, isFetching } = useQuery({
        queryKey: ['products', 'search', searchQuery, filters, displayLimit, isDefaultView, isMobile],
        queryFn: async () => {
            if (isDefaultView) {
                return apiClient.entities.Product.showcase(isMobile ? 2 : 4);
            }

            // Pass pagination/filters to backend
            // Map showNewOnly to sort order if single brand? Or just use default sort.
            // If showNewOnly is true, we prefer recent items.
            const sort = filters.is_new ? '-created_at' : undefined;

            // Construct API filters
            const apiFilters = {
                ...filters, // minPrice, maxPrice, brands, etc.
                q: searchQuery,
                limit: displayLimit,
                exclude_seen: 'false', // We manage "seen" logic or just show all for search
                new: filters.is_new // Pass the "new" flag to backend
            };

            return apiClient.entities.Product.list(sort, apiFilters);
        },
        placeholderData: keepPreviousData, // Keep old data while fetching new limits (v5 syntax)
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
    });

    const handleEmptyDelete = () => { };

    // Group products by brand for display grouping (used in discovery mode)
    const allProductsByBrand = React.useMemo(() => products.reduce((acc, product) => {
        const b = product.brand || 'Autre';
        if (!acc[b]) acc[b] = [];
        acc[b].push(product);
        return acc;
    }, {}), [products]);

    // Fetch dynamic category rules
    const { data: rulesData } = useQuery({
        queryKey: ['categoryRules'],
        queryFn: () => apiClient.entities.UserCategory.getSystemRules().catch(() => []),
        staleTime: 1000 * 60 * 5,
    });
    const rules = rulesData || [];

    // Fetch Global Metadata (Max Price, Brands, etc.)
    const { data: metadata } = useQuery({
        queryKey: ['metadata', filters.search], // depend on search for context-aware brands?
        queryFn: () => apiClient.entities.Product.getMetadata(),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30, // 30 minutes
        enabled: showFilters || !isDefaultView, // Only fetch when filters are shown or when a search is active
    });




    // Load More Logic
    const loadMore = useCallback(() => {
        // Don't trigger if already fetching
        if (isFetching) return;

        // Check against totalCount from backend
        const total = products.totalCount;



        // If totalCount is undefined/null, we can't paginate properly - stop loading
        // Also stop if we already have at least as many as the total
        if (total === undefined || total === null) return;
        if (products.length >= total) return;

        // Increment limit to load more products
        setDisplayLimit(prev => prev + 24);
    }, [products.totalCount, products.length, isFetching, displayLimit]);

    // Calculate hasMore for infinite scroll
    const hasMore = products.totalCount ? products.length < products.totalCount : false;

    // Use scroll-based infinite loading
    useInfiniteScroll(loadMore, isFetching, hasMore);

    // With server-side search, productsToDisplay IS products
    const productsToDisplay = products;

    if (isLoading) {
        return (
            <div className="min-h-screen p-4 md:p-8" aria-busy="true" aria-label="Chargement de la recherche">
                <div className="max-w-7xl mx-auto">
                    {/* Header skeleton */}
                    <div className="mb-8">
                        <div className="skeleton-bone skeleton-stagger-1 mb-6" style={{ height: '1.875rem', width: '9rem' }} />
                        <div className="flex gap-3 items-center">
                            <div className="skeleton-bone skeleton-rounded-lg skeleton-stagger-2" style={{ height: '3rem', flex: 1, maxWidth: '42rem' }} />
                            <div className="skeleton-bone skeleton-rounded-lg skeleton-filter-btn skeleton-stagger-3" />
                        </div>
                        <div className="skeleton-bone skeleton-stagger-3 mt-4" style={{ height: '0.875rem', width: '8rem' }} />
                    </div>
                    {/* Discovery sections skeleton (2 brand blocks) */}
                    {[0, 1].map((section) => (
                        <div key={section} className="mb-12">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className={`skeleton-bone skeleton-stagger-${section * 2 + 1}`} style={{ height: '1.5rem', width: '8rem' }} />
                                    <div className={`skeleton-bone skeleton-stagger-${section * 2 + 2} mt-1`} style={{ height: '0.75rem', width: '5rem' }} />
                                </div>
                                <div className={`skeleton-bone skeleton-rounded-lg skeleton-stagger-${section * 2 + 3}`} style={{ height: '2rem', width: '5rem' }} />
                            </div>
                            <SkeletonGrid count={4} columns="grid-cols-2 md:grid-cols-3 lg:grid-cols-4" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                        {!isDefaultView && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleReset}
                                className="mr-2"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </Button>
                        )}
                        <h1
                            onClick={handleReset}
                            className={`text-3xl font-bold text-gray-900 dark:text-white cursor-pointer hover:opacity-80 transition-opacity`}
                        >
                            Recherche
                        </h1>
                    </div>
                    <div className="flex gap-3 items-center">
                        <div className="relative flex-1 max-w-2xl">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <Input
                                placeholder="Rechercher une marque, produit, description..."
                                value={localQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                className="pl-12 pr-10 h-12 text-base"
                            />
                            {localQuery && (
                                <button
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => setShowFilters(true)}
                            className="gap-2 h-12"
                        >
                            <Filter className="w-4 h-4" />
                            Filtres
                        </Button>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                        <p className="text-gray-600">
                            {products.totalCount || 0} produit{(products.totalCount || 0) != 1 ? 's' : ''} trouvé{(products.totalCount || 0) != 1 ? 's' : ''}
                        </p>
                        {filters.brands.length === 1 && (
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                <Button
                                    variant={filters.is_new ? "outline" : "default"}
                                    onClick={() => setShowNewOnlyWrapper(false)}
                                    size="sm"
                                    className="w-full sm:w-auto"
                                >
                                    Voir tout le site
                                </Button>
                                <Button
                                    variant={filters.is_new ? "default" : "outline"}
                                    onClick={() => setShowNewOnlyWrapper(true)}
                                    size="sm"
                                    className="w-full sm:w-auto"
                                >
                                    Voir seulement les nouveautés
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {isDefaultView ? (
                    <div className="space-y-12">
                        {Object.entries(allProductsByBrand)
                            .sort(([, productsA], [, productsB]) => {
                                const getLatest = (list) => {
                                    const times = list.map(p => new Date(p.created_at || p.created_date).getTime()).filter(t => !isNaN(t));
                                    return times.length > 0 ? Math.max(...times) : 0;
                                };
                                const latestA = getLatest(productsA);
                                const latestB = getLatest(productsB);
                                return latestB - latestA;
                            })
                            .map(([brand, brandProducts]) => {
                                const sortedProducts = [...brandProducts].sort((a, b) => new Date(b.created_at || b.created_date) - new Date(a.created_at || a.created_date));
                                const limit = isMobile ? 2 : 4;
                                const latestProducts = sortedProducts.slice(0, limit);
                                if (latestProducts.length === 0) return null;
                                return (
                                    <div key={brand}>
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {brand}
                                                </h2>
                                                <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Nouveautés</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40"
                                                onClick={() => handleSeeMore(brandProducts[0]?.brand_slug || brand)}
                                            >
                                                Voir plus
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                                            <AnimatePresence>
                                                {latestProducts.map(product => (
                                                    <MatchCard
                                                        key={product.id}
                                                        product={product}
                                                        onDelete={handleEmptyDelete}
                                                        isNew={product.is_new}
                                                        source="search"
                                                    />
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                ) : products.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Search className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                Aucun résultat
                            </h2>
                            {filters.is_new && (
                                <div className="mt-2">
                                    <Button
                                        onClick={() => setShowNewOnlyWrapper(false)}
                                        variant="link"
                                    >
                                        Voir les anciens produits
                                    </Button>
                                </div>
                            )}
                            <p className="text-gray-600 mt-2">
                                Essayez d'autres mots-clés ou ajustez vos filtres
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6">
                        {/* Render only productsToDisplay */}
                        <AnimatePresence>
                            {productsToDisplay.map((product, index) => {
                                return (
                                    <div key={product.id}>
                                        <MatchCard
                                            product={product}
                                            onDelete={handleEmptyDelete}
                                            isNew={product.is_new}
                                            source="search"
                                        />
                                    </div>
                                );
                            })}
                        </AnimatePresence>
                        {isFetching && products.length < (products.totalCount || 0) && (
                            <div className="col-span-full py-8 flex justify-center">
                                <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Dialog open={showFilters} onOpenChange={setShowFilters}>
                <DialogContent className="sm:max-w-md p-0">
                    <FilterPanel
                        filters={filters}
                        setFilters={setFilters}
                        onClose={() => setShowFilters(false)}
                        products={products}
                        availableOptions={{
                            ...metadata,
                            categoryRules: rules // Ensure rules are passed (metadata might also have them, but rulesData is strictly rules)
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}